/**
 * HPC Jobs Service
 *
 * Handles fetching HPC job data from the hpc-jobs proxy Lambda via the
 * API Gateway. Requires an authenticated user with a valid Auth0 access token.
 */

// Cached API base URL loaded from aws-exports.json
let API_BASE_URL = ""

/**
 * Load the hpc-jobs API base URL from aws-exports.json configuration.
 *
 * Uses VITE_API_URL env var if set, otherwise falls back to the apiUrl
 * from aws-exports.json, and finally to a relative path.
 *
 * @returns The base URL for hpc-jobs API endpoints (e.g. "https://host/api/hpc-jobs").
 */
async function loadApiBaseUrl(): Promise<string> {
  if (API_BASE_URL) {
    return API_BASE_URL
  }

  try {
    const response = await fetch("/aws-exports.json")
    const config = await response.json()
    const baseUrl: string = import.meta.env.VITE_API_URL || config.apiUrl || ""
    API_BASE_URL = baseUrl
      ? `${baseUrl}/api/hpc-jobs`
      : "/api/hpc-jobs"
    return API_BASE_URL
  } catch (error) {
    console.error("Failed to load API URL from aws-exports.json:", error)
    throw new Error("HPC Jobs API URL not configured")
  }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Response shape from GET /api/hpc-jobs/metadata */
export interface HpcJobsMetadataResponse {
  /** The authenticated user's username. */
  username: string | null
  /** Whether the user is a manager (can view all users' jobs). */
  is_manager: boolean
}

/** Response shape from GET /api/hpc-jobs/jobs */
export interface ListJobsResponse {
  jobs: Record<string, unknown>[]
  count: number
}

/** Parameters for the list jobs endpoint. */
export interface ListJobsParams {
  /** Filter by user. Omit or "all" to show all users. */
  user?: string
  /** Whether to include finished jobs. */
  finished?: boolean
  /** Maximum number of jobs to return. */
  limit?: number
  /** Whether to include pricing data (total_usd) in the response. */
  includePrice?: boolean
  /**
   * Earliest job submission date to include, as an ISO date string
   * (YYYY-MM-DD). The backend excludes jobs submitted before this date.
   */
  start?: string
  /**
   * Latest job submission date to include, as an ISO date string
   * (YYYY-MM-DD). The backend defaults this to "now" when omitted.
   */
  end?: string
}

/**
 * One workflow as returned by GET /api/hpc-jobs/workflows.
 *
 * The output of `awsmap batch workflows --json`: snake_case field names and
 * epoch-millisecond timestamps. Declared as a type alias rather than an
 * interface so it stays assignable to Record<string, unknown>.
 */
export type WorkflowListingEntry = {
  /** Workflow ID, `wf-<UUID>`. */
  workflow_id: string
  /** Name from `workflow.py`, or null for some older workflows. */
  workflow_name: string | null
  /** Username of the person who submitted the workflow. */
  user: string
  /** Kind of workflow: "maphpc", "mosquito", "mosquito_dev" or "image_build". */
  workflow_kind: string
  /** Workflow status (matches the WorkflowStatus enum). */
  status: string
  /** Submission time in epoch milliseconds. */
  submitted_at: number
  /** When the first task started running, or null. */
  started_at: number | null
  /** When the workflow reached a final status, or null. */
  completed_at: number | null
  /** Number of tasks; an array job counts as one. */
  task_count: number
  /** Number of tasks per job status. */
  task_state_counts: Record<string, number>
  /** Tasks not finished yet. */
  active_task_count: number
  /** Tasks finished. */
  completed_task_count: number
  /** When the status and counts were last recalculated (epoch ms). */
  counts_refreshed_at: number
  /** Earliest time of the next refresh (epoch ms), or null once finalised. */
  next_refresh_at: number | null
  /**
   * Estimated compute cost so far (USD), from submit-time hourly rates and task
   * runtimes. Null when no task has a rate (image builds, older workflows).
   * Optional in the type because awsmap versions before the cost estimate omit it.
   */
  estimated_cost_usd?: number | null
}

/** Response shape from GET /api/hpc-jobs/workflows */
export interface ListWorkflowsResponse {
  /** The workflows, newest first. */
  workflows: WorkflowListingEntry[]
  /** Number of workflows returned. */
  count: number
  /** True when more workflows matched than the limit; only the newest are returned. */
  truncated: boolean
}

/** Parameters for the list workflows endpoint. */
export interface ListWorkflowsParams {
  /** A username, or "all" for every user (managers only). Required. */
  user: string
  /**
   * Whether to include finished workflows. Without it only active workflows
   * (SUBMITTED, RUNNING, EXPORTING) are returned.
   */
  finished?: boolean
  /**
   * Earliest submission time to include, as an ISO date (YYYY-MM-DD). The
   * backend defaults it to 14 days before `end`.
   */
  start?: string
  /** Latest submission time to include, as an ISO date. Defaults to now. */
  end?: string
  /** Maximum number of workflows to return. The backend defaults it to 200. */
  limit?: number
}

/** Response shape from GET /api/hpc-jobs/workflow-jobs */
export interface ListWorkflowJobsResponse {
  jobs: Record<string, unknown>[]
  count: number
}

/** Parameters for the list workflow jobs endpoint. */
export interface ListWorkflowJobsParams {
  /** User, required for auth */
  user: string
  /** The workflow ID to query. */
  workflowId: string
  /** Whether to include pricing data (total_usd) in the response. */
  includePrice?: boolean
}

/** Response shape from GET /api/hpc-jobs/logs */
export interface GetLogsResponse {
  logEvents: Array<{
    timestamp: number
    ingestionTime: number
    message: string
  }>
  newestLogIngestionTime: number | null
}

/** Parameters for the get logs endpoint. */
export interface GetLogsParams {
  /** The Batch job ID to fetch logs for. Required. */
  jobId: string
  /** Whether to return logs from the head (oldest first). */
  head?: boolean
  /** Maximum number of log events to return. */
  limit?: number
  /** Start time filter (epoch ms or ISO string). */
  startTime?: string
  /** End time filter (epoch ms or ISO string). */
  endTime?: string
}

/**
 * A single job entry returned by the status and array-child-jobs endpoints.
 *
 * The upstream awsmap lambdas use the awsmap CLI's snake_case field names and
 * epoch-millisecond timestamps:
 *
 *   {"name":"INLA_Use_Sampling","job_id":"c847ce4b-...:100","start_time":1786091279618,
 *    "end_time":1786092543992,"user":"etan","status":"FAILED","status_reason":"...",
 *    "array_size":null,"array_statuses":null,"workflow_id":"wf-6f2df4b9-...",
 *    "workflow_name":"MITN_run","after":["25c6f714-..."],"total_usd":0.4426719775,
 *    "usd_per_hour":0.1359}
 *
 * Declared as a type alias rather than an interface so it stays assignable to
 * Record<string, unknown> for transformJobData().
 */
export type JobStatusEntry = {
  /** Human-readable job name. */
  name: string
  /** Job ID, or 'parentJobId:childIndex' for an array job child. */
  job_id: string
  /** Job start time in epoch milliseconds, or null if it has not started. */
  start_time: number | null
  /** Job end time in epoch milliseconds, or null if it is still running. */
  end_time: number | null
  /** Username of the job owner. */
  user: string
  /** Current job status (matches the JobStatus enum). */
  status: string
  /** Reason for the current status, e.g. a failure message. */
  status_reason: string | null
  /** Number of children for an array job, or null for a normal job. */
  array_size: number | null
  /** Per-status child counts for an array job, or null for a normal job. */
  array_statuses: Record<string, number> | null
  /** Workflow ID grouping related jobs, or null if the job is standalone. */
  workflow_id: string | null
  /** Human-readable workflow name, or null if the job is standalone. */
  workflow_name: string | null
  /** Job IDs this job depends on, or null if it has no dependencies. */
  after: string[] | null
  /** Total cost of the job in USD, or null when no pricing data is available. */
  total_usd: number | null
  /** Hourly run rate of the job in USD, or null when no pricing data is available. */
  usd_per_hour: number | null
}

/**
 * Response shape from GET /api/hpc-jobs/status.
 *
 * The endpoint always returns an array. Querying a single job (or a single array
 * job child) yields one entry; querying an array job parent yields one entry per
 * child task, and does not include the parent itself.
 */
export type GetStatusResponse = JobStatusEntry[]

/**
 * Response shape from GET /api/hpc-jobs/array-child-jobs.
 *
 * One entry per child task of the requested array job parent.
 */
export type ListArrayChildJobsResponse = JobStatusEntry[]

/** Parameters for the list array child jobs endpoint. */
export interface ListArrayChildJobsParams {
  /** The parent array job ID whose child tasks should be listed. */
  jobId: string
  /** Whether to include pricing data (total_usd) in the response. */
  includePrice?: boolean
}

/** Response shape from POST /api/hpc-jobs/cancel */
export interface CancelJobResponse {
  [key: string]: unknown
}

// ---------------------------------------------------------------------------
// API Functions
// ---------------------------------------------------------------------------

/**
 * List HPC Batch jobs.
 *
 * @param params - Query parameters for filtering jobs.
 * @param accessToken - Auth0 access token for API authentication.
 * @returns The parsed list jobs response.
 * @throws Error if the request fails or returns a non-OK status.
 */
export async function listJobs(
  params: ListJobsParams,
  accessToken: string
): Promise<ListJobsResponse> {
  const baseUrl = await loadApiBaseUrl()
  const searchParams = new URLSearchParams()

  if (params.user) searchParams.set("user", params.user)
  if (params.finished !== undefined) searchParams.set("finished", String(params.finished))
  if (params.limit !== undefined) searchParams.set("limit", String(params.limit))
  if (params.includePrice !== undefined) searchParams.set("includePrice", String(params.includePrice))
  // ISO date (YYYY-MM-DD) bounds for the job submission window. Only sent when
  // provided; the backend defaults the end of the window to "now".
  if (params.start) searchParams.set("start", params.start)
  if (params.end) searchParams.set("end", params.end)

  const url = `${baseUrl}/jobs?${searchParams.toString()}`

  const response = await fetch(url, {
    method: "GET",
    headers: { Authorization: `Bearer ${accessToken}` },
  })

  if (!response.ok) {
    const body = await response.text()
    throw new Error(`List jobs failed (${response.status}): ${body}`)
  }

  return response.json()
}

/**
 * List workflows, one entry per workflow, newest first.
 *
 * Image build workflows are never requested, so they are not listed.
 *
 * @param params - Query parameters for filtering workflows.
 * @param accessToken - Auth0 access token for API authentication.
 * @returns The parsed list workflows response.
 * @throws Error if the request fails, returns a non-OK status, or returns a
 *   body without a workflows array and truncated flag.
 */
export async function listWorkflows(
  params: ListWorkflowsParams,
  accessToken: string
): Promise<ListWorkflowsResponse> {
  const baseUrl = await loadApiBaseUrl()
  const searchParams = new URLSearchParams()

  searchParams.set("user", params.user)
  if (params.finished !== undefined) searchParams.set("finished", String(params.finished))
  if (params.start) searchParams.set("start", params.start)
  if (params.end) searchParams.set("end", params.end)
  if (params.limit !== undefined) searchParams.set("limit", String(params.limit))

  const url = `${baseUrl}/workflows?${searchParams.toString()}`

  const response = await fetch(url, {
    method: "GET",
    headers: { Authorization: `Bearer ${accessToken}` },
  })

  if (!response.ok) {
    const body = await response.text()
    throw new Error(`List workflows failed (${response.status}): ${body}`)
  }

  const body = (await response.json()) as Partial<ListWorkflowsResponse> | null

  // Fail loudly if the shape changed, rather than rendering an empty table.
  if (!Array.isArray(body?.workflows) || typeof body?.truncated !== "boolean") {
    throw new Error(
      `List workflows returned an unexpected shape: ${JSON.stringify(body).slice(0, 200)}`
    )
  }

  return body as ListWorkflowsResponse
}

/**
 * List jobs within a specific workflow.
 *
 * @param params - Parameters including workflowId and optional includePrice flag.
 * @param accessToken - Auth0 access token for API authentication.
 * @returns The parsed workflow jobs response.
 * @throws Error if the request fails or returns a non-OK status.
 */
export async function listWorkflowJobs(
  params: ListWorkflowJobsParams,
  accessToken: string
): Promise<ListWorkflowJobsResponse> {
  const baseUrl = await loadApiBaseUrl()
  const searchParams = new URLSearchParams()
  searchParams.set("workflowId", params.workflowId)
  if (params.user) searchParams.set("user", params.user)
  if (params.includePrice !== undefined) searchParams.set("includePrice", String(params.includePrice))

  const url = `${baseUrl}/workflow-jobs?${searchParams.toString()}`

  const response = await fetch(url, {
    method: "GET",
    headers: { Authorization: `Bearer ${accessToken}` },
  })

  if (!response.ok) {
    const body = await response.text()
    throw new Error(`List workflow jobs failed (${response.status}): ${body}`)
  }

  return response.json()
}

/**
 * Get the status of an HPC job.
 *
 * @param jobId - The job ID to query. An array job parent ID returns one entry
 *   per child task; a plain job ID or a 'parentJobId:childIndex' child ID
 *   returns a single entry.
 * @param accessToken - Auth0 access token for API authentication.
 * @returns The status entries for the requested job, which may be empty if the
 *   job is unknown to the HPC account.
 * @throws Error if the request fails, returns a non-OK status, or returns a
 *   body that is not an array of job objects.
 */
export async function getJobStatus(
  jobId: string,
  accessToken: string
): Promise<GetStatusResponse> {
  const baseUrl = await loadApiBaseUrl()
  const url = `${baseUrl}/status?jobId=${encodeURIComponent(jobId)}`

  const response = await fetch(url, {
    method: "GET",
    headers: { Authorization: `Bearer ${accessToken}` },
  })

  if (!response.ok) {
    const body = await response.text()
    throw new Error(`Get job status failed (${response.status}): ${body}`)
  }

  return parseJobEntries(await response.json(), "Get job status")
}

/**
 * List the child tasks of an array job.
 *
 * @param params - Parameters including the parent jobId and optional includePrice flag.
 * @param accessToken - Auth0 access token for API authentication.
 * @returns One entry per child task, which may be empty if the job has no
 *   children or is unknown to the HPC account.
 * @throws Error if the request fails, returns a non-OK status, or returns a
 *   body that is not an array of job objects.
 */
export async function listArrayChildJobs(
  params: ListArrayChildJobsParams,
  accessToken: string
): Promise<ListArrayChildJobsResponse> {
  const baseUrl = await loadApiBaseUrl()
  const searchParams = new URLSearchParams()
  searchParams.set("jobId", params.jobId)
  if (params.includePrice !== undefined) searchParams.set("includePrice", String(params.includePrice))

  const url = `${baseUrl}/array-child-jobs?${searchParams.toString()}`

  const response = await fetch(url, {
    method: "GET",
    headers: { Authorization: `Bearer ${accessToken}` },
  })

  if (!response.ok) {
    const body = await response.text()
    throw new Error(`List array child jobs failed (${response.status}): ${body}`)
  }

  return parseJobEntries(await response.json(), "List array child jobs")
}

/**
 * Extracts the job entries from a status or array-child-jobs response body.
 *
 * Both endpoints return a bare JSON array. The other list endpoints in the HPC
 * account wrap their results in a {jobs, count} envelope, so that form is
 * accepted as well. Anything else throws, rather than leaving the caller to
 * render a table or panel of blank fields.
 *
 * @param body - The parsed JSON response body.
 * @param endpointLabel - Human-readable endpoint name used in the error message.
 * @returns The job entries from the response.
 * @throws Error if the body is neither an array of jobs nor a {jobs} envelope.
 */
function parseJobEntries(body: unknown, endpointLabel: string): JobStatusEntry[] {
  let entries: JobStatusEntry[] | null = null

  if (Array.isArray(body)) {
    entries = body as JobStatusEntry[]
  } else if (Array.isArray((body as { jobs?: unknown } | null)?.jobs)) {
    entries = (body as { jobs: JobStatusEntry[] }).jobs
  }

  // An entry without a job_id means the fields moved, which would otherwise show
  // up as an empty table rather than an error.
  if (entries === null || (entries.length > 0 && !entries[0]?.job_id)) {
    throw new Error(
      `${endpointLabel} returned an unexpected shape: expected an array of jobs, got ${JSON.stringify(body).slice(0, 200)}`
    )
  }

  return entries
}

/**
 * Get log events for a specific HPC job.
 *
 * @param params - Query parameters including jobId and optional filters.
 * @param accessToken - Auth0 access token for API authentication.
 * @returns The parsed logs response.
 * @throws Error if the request fails or returns a non-OK status.
 */
export async function getJobLogs(
  params: GetLogsParams,
  accessToken: string
): Promise<GetLogsResponse> {
  const baseUrl = await loadApiBaseUrl()
  const searchParams = new URLSearchParams()

  searchParams.set("jobId", params.jobId)
  if (params.head !== undefined) searchParams.set("head", String(params.head))
  if (params.limit !== undefined) searchParams.set("limit", String(params.limit))
  if (params.startTime) searchParams.set("startTime", params.startTime)
  if (params.endTime) searchParams.set("endTime", params.endTime)

  const url = `${baseUrl}/logs?${searchParams.toString()}`

  const response = await fetch(url, {
    method: "GET",
    headers: { Authorization: `Bearer ${accessToken}` },
  })

  if (!response.ok) {
    const body = await response.text()
    throw new Error(`Get job logs failed (${response.status}): ${body}`)
  }

  return response.json()
}


/**
 * Cancel a running HPC job or an entire workflow.
 *
 * Sends a POST request to the cancel endpoint. The backend accepts either an
 * AWS Batch job ID or a workflow ID as the identifier, and enforces that
 * non-manager users can only cancel jobs they own.
 *
 * @param jobId - The job ID or workflow ID to cancel.
 * @param accessToken - Auth0 access token for API authentication.
 * @returns The parsed cancel response from the backend.
 * @throws Error if the request fails or returns a non-OK status.
 */
export async function cancelJob(
  jobId: string,
  accessToken: string
): Promise<CancelJobResponse> {
  const baseUrl = await loadApiBaseUrl()
  const url = `${baseUrl}/cancel?jobId=${encodeURIComponent(jobId)}`

  const response = await fetch(url, {
    method: "POST",
    headers: { Authorization: `Bearer ${accessToken}` },
  })

  if (!response.ok) {
    const body = await response.text()
    throw new Error(`Cancel job failed (${response.status}): ${body}`)
  }

  return response.json()
}

/**
 * Get metadata for the current user's HPC jobs access.
 *
 * Returns information about the user's permissions, including whether they
 * are a manager (can view all users' jobs) and their username.
 *
 * @param accessToken - Auth0 access token for API authentication.
 * @returns The parsed metadata response.
 * @throws Error if the request fails or returns a non-OK status.
 */
export async function getHpcJobsMetadata(
  accessToken: string
): Promise<HpcJobsMetadataResponse> {
  const baseUrl = await loadApiBaseUrl()
  const url = `${baseUrl}/metadata`

  const response = await fetch(url, {
    method: "GET",
    headers: { Authorization: `Bearer ${accessToken}` },
  })

  if (!response.ok) {
    const body = await response.text()
    throw new Error(`Get HPC jobs metadata failed (${response.status}): ${body}`)
  }

  return response.json()
}
